import { getAuth } from "firebase/auth";

export interface SessionInfo {
  loginTime: number;
  lastActivity: number;
  sessionId: string;
  maxAge: number; // in milliseconds
}

// Session configuration
const SESSION_CONFIG = {
  MAX_AGE: 12 * 60 * 60 * 1000, // 12 hours
  ACTIVITY_CHECK_INTERVAL: 30 * 60 * 1000, // 30 minutes
  ACTIVITY_TIMEOUT: 2 * 60 * 60 * 1000, // 2 hours of inactivity
};

// Generate unique session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Create session info
export const createSession = (): SessionInfo => {
  const now = Date.now();
  const session: SessionInfo = {
    loginTime: now,
    lastActivity: now,
    sessionId: generateSessionId(),
    maxAge: SESSION_CONFIG.MAX_AGE,
  };

  sessionStorage.setItem("session_info", JSON.stringify(session));
  return session;
};

// Get current session
export const getCurrentSession = (): SessionInfo | null => {
  try {
    const sessionData = sessionStorage.getItem("session_info");
    return sessionData ? JSON.parse(sessionData) : null;
  } catch (error) {
    return null;
  }
};

// Update last activity
export const updateActivity = (): void => {
  const session = getCurrentSession();
  if (session) {
    session.lastActivity = Date.now();
    sessionStorage.setItem("session_info", JSON.stringify(session));
  }
};

// Check if session is valid
export const isSessionValid = (): { valid: boolean; reason?: string } => {
  const session = getCurrentSession();

  if (!session) {
    return { valid: false, reason: "No session found" };
  }

  const now = Date.now();

  // Check max age (12 hours)
  if (now - session.loginTime > session.maxAge) {
    return { valid: false, reason: "Session expired (max age)" };
  }

  // Check activity timeout (2 hours of inactivity)
  if (now - session.lastActivity > SESSION_CONFIG.ACTIVITY_TIMEOUT) {
    return { valid: false, reason: "Session expired (inactivity)" };
  }

  return { valid: true };
};

// Clear session
export const clearSession = async (): Promise<void> => {
  try {
    // Clear sessionStorage
    const itemsToRemove = [
      "session_info",
      "firebase_token",
      "user_login_time",
      "user",
      "accessToken",
    ];

    // itemsToRemove.forEach((item) => {
    //   sessionStorage.removeItem(item);
    // });

    // Clear localStorage as backup
    const localStorageItems = [
      "user",
      "accessToken",
      "firebase_token",
      "user_login_time",
    ];
    localStorageItems.forEach((item) => {
      localStorage.removeItem(item);
    });

    // Sign out from Firebase
    const auth = getAuth();
    if (auth.currentUser) {
      await auth.signOut();
    }
  } catch (error) {}
};

// Session validation with automatic cleanup
export const validateAndCleanupSession = async (): Promise<boolean> => {
  const validation = isSessionValid();

  if (!validation.valid) {
    await clearSession();

    // Redirect to login
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }

    return false;
  }

  // Update activity for valid sessions
  updateActivity();
  return true;
};

// Get session status for debugging
export const getSessionStatus = () => {
  const session = getCurrentSession();
  if (!session) return null;

  const now = Date.now();
  return {
    sessionId: session.sessionId,
    age: now - session.loginTime,
    maxAge: session.maxAge,
    timeLeft: session.maxAge - (now - session.loginTime),
    lastActivity: now - session.lastActivity,
    activityTimeout: SESSION_CONFIG.ACTIVITY_TIMEOUT,
    isValid: isSessionValid().valid,
  };
};
